import React from "react";
import { motion } from "framer-motion";
import { Star, Flame, Target } from "lucide-react";

// Niveles de Bloom con colores semáforo
const BLOOM_LEVELS = [
  { id: "recordar", name: "Recordar", points: 5, color: "#ef4444", bgColor: "bg-red-500" },
  { id: "comprender", name: "Comprender", points: 10, color: "#f97316", bgColor: "bg-orange-500" },
  { id: "aplicar", name: "Aplicar", points: 15, color: "#eab308", bgColor: "bg-yellow-500" },
  { id: "analizar", name: "Analizar", points: 20, color: "#22c55e", bgColor: "bg-green-500" },
  { id: "evaluar", name: "Evaluar", points: 25, color: "#3b82f6", bgColor: "bg-blue-500" },
  { id: "crear", name: "Crear", points: 30, color: "#8b5cf6", bgColor: "bg-purple-500" },
];

export const BloomThermometer = ({ 
  currentLevel = "recordar", 
  sessionPoints = 0,
  totalPoints = 0,
  streak = 0 
}) => {
  // Encontrar el índice del nivel actual
  const currentIndex = BLOOM_LEVELS.findIndex(l => l.id === currentLevel.toLowerCase());
  const activeLevel = BLOOM_LEVELS[currentIndex] || BLOOM_LEVELS[0];

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4" data-testid="bloom-thermometer">
      {/* Header con stats */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Nivel Taxonómico</span>
        </div>
        <div className="flex items-center gap-3">
          {/* Streak */}
          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-orange-50">
            <Flame className="w-3.5 h-3.5 text-orange-500" />
            <span className="text-xs font-semibold text-orange-600">{streak}</span>
          </div>
          {/* Points */}
          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-purple-50">
            <Star className="w-3.5 h-3.5 text-purple-500" />
            <span className="text-xs font-semibold text-purple-600">{totalPoints}</span>
          </div>
        </div>
      </div>

      {/* Termómetro vertical */}
      <div className="flex gap-3">
        {/* Barra vertical del termómetro */}
        <div className="relative w-3 bg-gray-100 rounded-full overflow-hidden" style={{ height: "180px" }}>
          {/* Relleno animado */}
          <motion.div
            className="absolute bottom-0 left-0 right-0 rounded-full"
            style={{ backgroundColor: activeLevel.color }}
            initial={{ height: 0 }}
            animate={{ height: `${((currentIndex + 1) / BLOOM_LEVELS.length) * 100}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>

        {/* Niveles */}
        <div className="flex-1 flex flex-col-reverse justify-between" style={{ height: "180px" }}>
          {BLOOM_LEVELS.map((level, index) => {
            const isActive = index <= currentIndex;
            const isCurrent = level.id === currentLevel.toLowerCase();
            
            return (
              <div
                key={level.id}
                className={`flex items-center gap-2 py-1 px-2 rounded-lg transition-all ${
                  isCurrent ? "bg-gray-50 scale-105" : ""
                }`}
              >
                {/* Indicador */}
                <div
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    isActive ? level.bgColor : "bg-gray-200"
                  } ${isCurrent ? "ring-2 ring-offset-1" : ""}`}
                  style={isCurrent ? { ringColor: level.color } : {}}
                />
                
                {/* Nombre del nivel */}
                <span
                  className={`text-xs font-medium flex-1 ${
                    isActive ? "text-gray-800" : "text-gray-400"
                  } ${isCurrent ? "font-bold" : ""}`}
                >
                  {level.name}
                </span>
                
                {/* Puntos */}
                <span
                  className={`text-xs ${
                    isActive ? "text-gray-600" : "text-gray-300"
                  }`}
                >
                  +{level.points}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Puntos de la sesión */}
      {sessionPoints > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 pt-3 border-t border-gray-100"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Puntos esta sesión</span>
            <span className="text-sm font-bold text-purple-600">+{sessionPoints}</span>
          </div>
        </motion.div>
      )}

      {/* Nivel actual destacado */}
      <div 
        className="mt-3 p-2 rounded-xl text-center"
        style={{ backgroundColor: `${activeLevel.color}15` }}
      >
        <span className="text-xs text-gray-600">Nivel actual: </span>
        <span 
          className="text-sm font-bold"
          style={{ color: activeLevel.color }}
        >
          {activeLevel.name}
        </span>
      </div>
    </div>
  );
};

// Versión compacta para el header del chat
export const BloomBadge = ({ currentLevel = "recordar", points = 0 }) => {
  const level = BLOOM_LEVELS.find(l => l.id === currentLevel.toLowerCase()) || BLOOM_LEVELS[0];
  
  return (
    <div className="flex items-center gap-2">
      <div 
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
        style={{ backgroundColor: `${level.color}15` }}
      >
        <div 
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: level.color }}
        />
        <span 
          className="text-xs font-semibold"
          style={{ color: level.color }}
        >
          {level.name}
        </span>
      </div>
      <div className="flex items-center gap-1 px-2 py-1.5 rounded-full bg-purple-50">
        <Star className="w-3 h-3 text-purple-500" />
        <span className="text-xs font-semibold text-purple-600">{points}</span>
      </div>
    </div>
  );
};

export default BloomThermometer;
